import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getSettingsSummary } from '../api/settingsApi';
import { useUser } from '../contexts/UserContext';

export const useTheme = () => {
  const { currentUser } = useUser();
  
  // Get user settings to extract theme preference
  const { data: settingsData } = useQuery({
    queryKey: ['settings-summary', currentUser?.email],
    queryFn: getSettingsSummary,
    enabled: !!currentUser?.email,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const userTheme = settingsData?.userSettings?.theme || 'auto';
  const userBackgroundImage = settingsData?.userSettings?.backgroundImage || 'none';

  useEffect(() => {
    const applyThemeAndBackground = (theme: string, backgroundImage: string) => {
      const root = document.documentElement;
      
      // Remove existing theme classes
      root.classList.remove('theme-light', 'theme-dark', 'theme-auto');
      
      // Apply new theme class
      root.classList.add(`theme-${theme}`);
      
      // Determine effective theme
      let effectiveTheme = theme;
      if (theme === 'auto') {
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        effectiveTheme = prefersDark ? 'dark' : 'light';
        root.setAttribute('data-effective-theme', effectiveTheme);
      } else {
        root.setAttribute('data-effective-theme', theme);
      }
      
      // Apply background image
      const rootElement = document.getElementById('root');
      if (rootElement) {
        if (backgroundImage === 'none' || !backgroundImage) {
          // Remove background image, keep only the color
          rootElement.style.background = 'var(--app-outer-bg)';
        } else {
          // Apply the selected background image
          const imageUrl = `/bg-${backgroundImage}.jpg`;
          rootElement.style.background = `var(--app-outer-bg) url('${imageUrl}')`;
          rootElement.style.backgroundSize = 'cover';
          rootElement.style.backgroundPosition = 'center';
          rootElement.style.backgroundAttachment = 'fixed';
          rootElement.style.backgroundRepeat = 'no-repeat';
        }
      }
    };

    applyThemeAndBackground(userTheme, userBackgroundImage);

    // Listen for system theme changes when in auto mode
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleSystemThemeChange = () => {
      if (userTheme === 'auto') {
        applyThemeAndBackground('auto', userBackgroundImage);
      }
    };

    mediaQuery.addEventListener('change', handleSystemThemeChange);
    return () => mediaQuery.removeEventListener('change', handleSystemThemeChange);
  }, [userTheme, userBackgroundImage]);

  return { currentTheme: userTheme };
};